import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuth } from '../../components/AuthProvider';
import AlertasDeDocumentos from '../../components/AlertasDeDocumentos';
import {
  compradorConRecurso,
  puedeAdministrarPadron,
  puedeAprobarSolicitudes,
  puedeBloquearDocumento,
} from '../../domain/permissions';
import { enlaceDeAvisoDeAcceso, telefonoParaWhatsapp } from '../../domain/whatsapp';
import DesempenoTrabajador from '../../components/DesempenoTrabajador';
import type { ProfileRow, Role, WorkerApplicationRow } from '../../lib/db-types';
import { generateTempPassword } from '../../lib/tempPassword';
import { normalizarDocumento } from '../../domain/loginIdentity';
import {
  approveApplication,
  actualizarFichaTrabajador,
  approveWorkersBulk,
  blockDocument,
  listApplications,
  listWorkers,
  rejectApplication,
  resetPassword,
  puedeGestionarClaves,
  estadoDeClaves,
  type EstadoDeClave,
  setStatus,
  signedFileUrl,
  updateRoles,
  translateApiError,
  type ApplicationStatus,
  type BulkWorkerInput,
  type WorkerStatus,
} from '../../api/workers';
import { miAccesoCompras } from '../../api/compras';
import TrabajadoresImportar from './TrabajadoresImportar';
import FilaTarjeta from '../../components/FilaTarjeta';
import TarjetasResumen from '../../components/TarjetasResumen';
import {
  cambiosDeFicha, fichaDesdePerfil, loQueFaltaEnFicha, nombresDeLosCambios,
  type FormFichaTrabajador,
} from '../../domain/fichaDeTrabajador';

interface DocumentEntry {
  type: string;
  path: string;
  issue_date: string | null;
  expiry_date: string | null;
}

const DOC_LABELS: Record<string, string> = {
  carnet_sanidad: 'Carné de sanidad',
  certificado_unico_laboral: 'Certificado único laboral',
  otro: 'Otro documento',
};

function FileLink({ path, label }: { path: string | null; label: string }) {
  const [busy, setBusy] = useState(false);
  if (!path) return <span className="text-xs text-gray-400">{label}: sin archivo</span>;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        const url = await signedFileUrl(path, 60);
        setBusy(false);
        if (url) window.open(url, '_blank', 'noopener');
        else alert('No se pudo abrir el archivo. Inténtalo de nuevo.');
      }}
      className="mr-3 text-xs text-blue-600 underline disabled:opacity-50"
    >
      {busy ? 'Abriendo…' : label}
    </button>
  );
}

function DetalleModal({
  app,
  onClose,
}: {
  app: WorkerApplicationRow;
  onClose: () => void;
}) {
  const docs = (app.documents ?? []) as DocumentEntry[];
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-lg">
        <h2 className="text-lg font-bold text-gray-900">
          {app.first_names} {app.last_names}
        </h2>
        <p className="mb-3 text-sm text-gray-500">
          {app.document_type} {app.dni} · {app.phone} · {app.email}
        </p>
        <dl className="mb-4 grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-gray-500">Nacimiento</dt>
            <dd>{app.birth_date ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Ubicación</dt>
            <dd>
              {[app.department, app.province, app.district].filter(Boolean).join(' / ') || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Banco / cuenta</dt>
            <dd>{[app.bank, app.account_number].filter(Boolean).join(' ') || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">CCI</dt>
            <dd>{app.cci ?? '—'}</dd>
          </div>
        </dl>
        <p className="mb-1 text-sm font-semibold text-gray-800">Fotos</p>
        <div className="mb-4">
          <FileLink path={app.photo_path} label="Foto personal" />
          <FileLink path={app.doc_front_path} label="Documento frontal" />
          <FileLink path={app.doc_back_path} label="Documento posterior" />
        </div>
        <p className="mb-1 text-sm font-semibold text-gray-800">Documentos laborales</p>
        {docs.length === 0 && <p className="text-xs text-gray-400">No adjuntó documentos.</p>}
        {docs.map((d) => (
          <div key={d.path} className="mb-2 rounded border border-gray-200 p-2">
            <p className="text-sm font-medium text-gray-800">{DOC_LABELS[d.type] ?? d.type}</p>
            <p className="mb-1 text-xs text-gray-500">
              Emisión: {d.issue_date ?? '—'} · Vence: {d.expiry_date ?? '—'}
            </p>
            <FileLink path={d.path} label="Ver archivo" />
          </div>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded border border-gray-300 py-2 text-sm text-gray-700"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}


/** Lo que hay que entregarle a una persona para que pueda entrar. */
export interface Credencial {
  nombre: string;
  /** Con esto entra: el login pide el documento, no el correo. */
  documento: string;
  telefono: string | null;
  clave: string;
  /** Campaña asignada, si ya se sabe. Se agregan al mensaje de WhatsApp. */
  campania?: string | null;
  tienda?: string | null;
  horario?: string | null;
}

/**
 * Ventana con la credencial recién creada.
 *
 * Se queda hasta que la cierren a mano y no se puede pasar por alto: **es el único momento en que
 * la clave existe en algo legible**. La base guarda un hash, así que si esta ventana se va sin que
 * nadie copie ni envíe, la clave se perdió y hay que volver a cambiarla.
 *
 * El botón de WhatsApp abre el chat con el mensaje ya escrito y lo envía la persona. Si el celular
 * no sirve como número peruano, el botón NO aparece: abrir un chat con un número inventado le
 * manda la clave de un trabajador a un desconocido.
 */
export function PanelCredencial({ datos, onClose }: { datos: Credencial; onClose: () => void }) {
  const [copiado, setCopiado] = useState(false);
  const enlace = enlaceDeAvisoDeAcceso({
    nombre: datos.nombre,
    documento: datos.documento,
    clave: datos.clave,
    url: window.location.origin,
    telefono: datos.telefono,
    campania: datos.campania,
    tienda: datos.tienda,
    horario: datos.horario,
  });
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
        <h2 className="text-lg font-bold text-gray-900">Acceso listo</h2>
        <p className="mb-3 text-sm text-gray-500">{datos.nombre}</p>
        <dl className="mb-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
          <dt className="text-xs text-gray-500">Entra con su documento</dt>
          <dd className="mb-2 font-mono">{datos.documento}</dd>
          <dt className="text-xs text-gray-500">Clave temporal</dt>
          <dd className="font-mono text-lg font-bold">{datos.clave}</dd>
        </dl>
        <p className="mb-4 text-xs text-amber-700">
          Esta clave no se puede volver a ver: envíasela o anótala ahora. El sistema <b>no</b> manda
          correos. Al entrar se le pedirá crear su propia clave.
        </p>
        {enlace ? (
          <a
            href={enlace}
            target="_blank"
            rel="noreferrer"
            className="mb-2 block w-full rounded bg-green-600 py-2 text-center text-sm font-medium text-white"
          >
            Enviar por WhatsApp
          </a>
        ) : (
          <p className="mb-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            Sin celular válido en su ficha: no se puede enviar por WhatsApp. Cópiala y entrégasela.
          </p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(datos.clave)
                .then(() => setCopiado(true))
                .catch(() => setCopiado(false));
            }}
            className="w-1/2 rounded border border-gray-300 py-2 text-sm text-gray-700"
          >
            {copiado ? 'Copiada ✓' : 'Copiar clave'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-1/2 rounded bg-blue-600 py-2 text-sm font-medium text-white"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}

/** Qué significa cada estado del filtro, para que la tarjeta del total diga DE QUÉ es el total. */
const ESTADO_SOLICITUD_DETALLE: Record<ApplicationStatus, string> = {
  pendiente: 'esperan revisión',
  aprobado: 'ya aprobadas',
  rechazado: 'rechazadas',
  bloqueado: 'bloqueadas',
};

/**
 * Si una fecha ISO cae dentro de los últimos N días.
 *
 * Compara INSTANTES y no fechas de calendario, así que no depende de la zona horaria: `created_at`
 * es un `timestamptz` y `Date.now()` es el mismo reloj. Para «cuántas entraron esta semana» eso
 * alcanza; si alguna vez hace falta cortar por día de Lima, el corte va con `date-fns-tz` como en
 * el resto del sistema.
 */
function esDeLosUltimosDias(iso: string, dias: number): boolean {
  const t = Date.parse(iso);
  return !Number.isNaN(t) && Date.now() - t <= dias * 24 * 60 * 60 * 1000;
}

export function SolicitudesTab() {
  const [status, setStatus] = useState<ApplicationStatus>('pendiente');
  const [rows, setRows] = useState<WorkerApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<WorkerApplicationRow | null>(null);
  const [aprobar, setAprobar] = useState<WorkerApplicationRow | null>(null);
  /** Credencial recién creada, en pantalla hasta que la entreguen. */
  const [credencial, setCredencial] = useState<Credencial | null>(null);
  const [rechazar, setRechazar] = useState<WorkerApplicationRow | null>(null);
  const [bloquear, setBloquear] = useState<WorkerApplicationRow | null>(null);
  const { roles: misRolesSolicitudes } = useAuth();
  /**
   * Bloquear es del jefe. No es «rechazar con énfasis»: deja el documento en lista negra y esa
   * persona no se puede volver a registrar en ninguna campaña, nunca. `block_document()` exige
   * admin en el servidor, así que esconder el botón evita ofrecer algo que rebota.
   */
  const puedeBloquear = puedeBloquearDocumento(misRolesSolicitudes);
  const [email, setEmail] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listApplications(status));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  function openAprobar(app: WorkerApplicationRow) {
    setEmail(app.email ?? '');
    setTempPassword(generateTempPassword());
    setOk(null);
    setError(null);
    setAprobar(app);
  }

  async function doAprobar() {
    if (!aprobar) return;
    setBusy(true);
    setError(null);
    try {
      await approveApplication(aprobar.id, email.trim(), tempPassword);
      /**
       * La credencial se queda EN PANTALLA con su botón de WhatsApp, en vez de un aviso de texto.
       *
       * El aviso decía «Correo: <el de contacto>», y eso era doblemente falso: ese no es el usuario
       * con el que la persona entra —entra con su documento— y además nunca se le envió nada por
       * correo, porque el sistema no tiene forma de mandar correos. Quien leía ese mensaje se iba
       * tranquilo pensando que la persona ya había sido avisada.
       */
      setCredencial({
        nombre: `${aprobar.first_names} ${aprobar.last_names}`,
        documento: aprobar.dni,
        telefono: aprobar.phone,
        clave: tempPassword,
      });
      setAprobar(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo aprobar.');
    } finally {
      setBusy(false);
    }
  }

  async function doRechazar() {
    if (!rechazar || motivo.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await rejectApplication(rechazar.id, motivo.trim());
      setOk('Solicitud rechazada.');
      setRechazar(null);
      setMotivo('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo rechazar.');
    } finally {
      setBusy(false);
    }
  }

  async function doBloquear() {
    if (!bloquear || motivo.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await blockDocument(bloquear.dni, motivo.trim());
      setOk(`Documento ${bloquear.dni} bloqueado. No podrá registrarse de nuevo.`);
      setBloquear(null);
      setMotivo('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo bloquear.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {/* Los vencimientos van ARRIBA del filtro a proposito: es lo unico de esta pantalla que tiene
          fecha limite, y puesto debajo de la tabla se lee despues de haber decidido a quien mirar. */}
      <AlertasDeDocumentos />
      <div className="mb-3 flex items-center gap-2">
        <label className="text-sm text-gray-600" htmlFor="filtro-estado">
          Estado
        </label>
        <select
          id="filtro-estado"
          value={status}
          onChange={(e) => setStatus(e.target.value as ApplicationStatus)}
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        >
          <option value="pendiente">Pendientes</option>
          <option value="aprobado">Aprobadas</option>
          <option value="rechazado">Rechazadas</option>
          <option value="bloqueado">Bloqueadas</option>
        </select>
        <button
          type="button"
          onClick={() => void load()}
          className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-700"
        >
          Actualizar
        </button>
      </div>

      {/* Lo que falta ANTES de aprobar. Una solicitud sin selfie o sin documento se aprueba igual
          —el sistema no lo impide— y después la persona no tiene foto en ninguna pantalla ni con
          qué acreditarse en la tienda; que el hueco esté contado acá es lo que hace que se pida el
          faltante mientras todavía se puede. */}
      {!loading && rows.length > 0 && (
        <TarjetasResumen indicadores={[
          { titulo: 'Solicitudes', valor: rows.length, detalle: ESTADO_SOLICITUD_DETALLE[status] },
          { titulo: 'Sin selfie', valor: rows.filter((a) => !a.photo_path).length,
            tono: 'atencion' },
          { titulo: 'Sin documento',
            valor: rows.filter((a) => !a.doc_front_path || !a.doc_back_path).length,
            tono: 'atencion', detalle: 'falta una cara o las dos' },
          { titulo: 'Sin adjuntos',
            valor: rows.filter((a) => (a.documents ?? []).length === 0).length,
            tono: 'atencion' },
          { titulo: 'Nuevas (7 días)',
            valor: rows.filter((a) => esDeLosUltimosDias(a.created_at, 7)).length },
        ]} />
      )}

      {ok && <p className="mb-3 rounded bg-green-50 p-2 text-sm text-green-800">{ok}</p>}
      {error && <p className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {loading && <p className="text-sm text-gray-500">Cargando solicitudes…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-sm text-gray-500">No hay solicitudes en este estado.</p>
      )}

      {/* En celular: una tarjeta por solicitud (sin desplazamiento horizontal). */}
      {!loading && rows.length > 0 && (
        <div className="space-y-2 lg:hidden">
          {rows.map((a) => (
            <FilaTarjeta
              key={a.id}
              titulo={`${a.first_names} ${a.last_names}`}
              campos={[
                { etiqueta: 'Documento', valor: `${a.document_type} ${a.dni}` },
                { etiqueta: 'Celular', valor: a.phone },
                { etiqueta: 'Correo', valor: a.email },
                {
                  etiqueta: 'Ubicación',
                  valor: [a.department, a.district].filter(Boolean).join(' / '),
                },
                {
                  etiqueta: 'Documentos adjuntos',
                  valor: ((a.documents ?? []) as DocumentEntry[]).length,
                },
              ]}
              acciones={
                <>
                  <button
                    type="button"
                    onClick={() => setDetalle(a)}
                    className="text-blue-600 underline"
                  >
                    Ver
                  </button>
                  {a.status === 'pendiente' && (
                    <>
                      <button
                        type="button"
                        onClick={() => openAprobar(a)}
                        className="rounded bg-green-600 px-3 py-1 text-xs text-white"
                      >
                        Aprobar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMotivo('');
                          setRechazar(a);
                        }}
                        className="rounded bg-amber-500 px-3 py-1 text-xs text-white"
                      >
                        Rechazar
                      </button>
                      {puedeBloquear && (
                        <button
                          type="button"
                          onClick={() => {
                            setMotivo('');
                            setBloquear(a);
                          }}
                          className="rounded bg-red-600 px-3 py-1 text-xs text-white"
                        >
                          Bloquear
                        </button>
                      )}
                    </>
                  )}
                </>
              }
            />
          ))}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="py-2">Documento</th>
                <th>Nombre</th>
                <th>Celular</th>
                <th>Correo</th>
                <th>Ubicación</th>
                <th>Docs.</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-b border-gray-100">
                  <td className="py-2">
                    {a.document_type} {a.dni}
                  </td>
                  <td>
                    {a.first_names} {a.last_names}
                  </td>
                  <td>{a.phone ?? '—'}</td>
                  <td>{a.email ?? '—'}</td>
                  <td>{[a.department, a.district].filter(Boolean).join(' / ') || '—'}</td>
                  <td>{((a.documents ?? []) as DocumentEntry[]).length}</td>
                  <td className="whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setDetalle(a)}
                      className="mr-2 text-blue-600 underline"
                    >
                      Ver
                    </button>
                    {a.status === 'pendiente' && (
                      <>
                        <button
                          type="button"
                          onClick={() => openAprobar(a)}
                          className="mr-2 rounded bg-green-600 px-2 py-1 text-xs text-white"
                        >
                          Aprobar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMotivo('');
                            setRechazar(a);
                          }}
                          className="mr-2 rounded bg-amber-500 px-2 py-1 text-xs text-white"
                        >
                          Rechazar
                        </button>
                        {puedeBloquear && (
                          <button
                            type="button"
                            onClick={() => {
                              setMotivo('');
                              setBloquear(a);
                            }}
                            className="rounded bg-red-600 px-2 py-1 text-xs text-white"
                          >
                            Bloquear
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detalle && <DetalleModal app={detalle} onClose={() => setDetalle(null)} />}

      {credencial && (
        <PanelCredencial datos={credencial} onClose={() => setCredencial(null)} />
      )}

      {aprobar && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900">Aprobar trabajador</h2>
            <p className="mb-3 text-sm text-gray-500">
              {aprobar.first_names} {aprobar.last_names} · {aprobar.document_type} {aprobar.dni}
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="ap-email">
              Correo de contacto
              <span className="ml-1 font-normal text-gray-400">(opcional)</span>
            </label>
            {/*
              Decía «Correo de acceso» y no lo es: la persona entra con su DOCUMENTO, y el correo
              de acceso lo arma el servidor. Además, junto a «Clave temporal», ese rótulo hacía
              creer que las credenciales se le enviaban por correo — algo que el sistema nunca ha
              podido hacer. Se entregan por WhatsApp desde la ventana siguiente.
            */}
            <p className="mb-1 text-xs text-gray-500">
              Solo para ubicarla. Entra con su documento {aprobar.dni}, no con el correo.
            </p>
            <input
              id="ap-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mb-3 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="ap-pass">
              Clave temporal
            </label>
            <div className="mb-4 flex gap-2">
              <input
                id="ap-pass"
                type="text"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => setTempPassword(generateTempPassword())}
                className="whitespace-nowrap rounded border border-gray-300 px-2 text-sm text-gray-700"
              >
                Generar
              </button>
            </div>
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => setAprobar(null)}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={busy || email.trim().length === 0 || tempPassword.length < 6}
                onClick={() => void doAprobar()}
                className="w-2/3 rounded bg-green-600 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {busy ? 'Creando usuario…' : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(rechazar || bloquear) && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900">
              {rechazar ? 'Rechazar solicitud' : 'Bloquear documento'}
            </h2>
            <p className="mb-3 text-sm text-gray-500">
              {rechazar
                ? 'El postulante podrá volver a registrarse con otro dato corregido.'
                : 'El documento entra a la lista negra: no podrá volver a registrarse y su usuario queda bloqueado.'}
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="motivo">
              Motivo (obligatorio)
            </label>
            <textarea
              id="motivo"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="mb-4 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setRechazar(null);
                  setBloquear(null);
                }}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={busy || motivo.trim().length === 0}
                onClick={() => (rechazar ? void doRechazar() : void doBloquear())}
                className={`w-2/3 rounded py-2 text-sm font-medium text-white disabled:opacity-50 ${
                  rechazar ? 'bg-amber-500' : 'bg-red-600'
                }`}
              >
                {busy ? 'Guardando…' : rechazar ? 'Rechazar' : 'Bloquear documento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ROLES: { value: Role; label: string }[] = [
  { value: 'admin', label: 'Jefe de operaciones' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'coordinador', label: 'Coordinador' },
  { value: 'recurso', label: 'Recurso' },
  // 0061. Sin esta linea el rol existe en la base y en la matriz de permisos, pero NO hay forma de
  // asignarselo a nadie desde el panel: quedaria como una funcion que solo se puede encender por
  // SQL, que es justo lo que el proyecto decidio no volver a hacer (BITACORA §3.22).
  { value: 'datos', label: 'Validador de datos' },
  // 0110. Es el rol que el usuario necesita para «asignar los formularios solo a ellos»: marcarlo
  // enciende `hace_ruta` por trigger, que es lo que el servidor ya usa para «Mi ruta» y para dejar
  // insertar fichas de campo. Una casilla, y el resto se acomoda solo.
  { value: 'supervisor', label: 'Supervisor' },
  // Cotizaciones Treid (0272 y 0273). Las dos casillas solo se ofrecen a quien administra compras: el trigger
  // profiles_guard_comprador rechaza a cualquier otro, también a los admin de campo. Ver `rolesEditables` en ActivosTab.
  { value: 'comprador', label: 'Comprador' },
  { value: 'solicitante', label: 'Solicitante' },
];

const STATUS_LABEL: Record<string, string> = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  bloqueado: 'Bloqueado',
};

// ---------------------------------------------------------------------------
// Alta MANUAL de un trabajador (2026-08-20)
//
// Hasta hoy solo había dos formas de que alguien entrara al sistema: que se registrara él mismo y
// alguien aprobara su solicitud, o la importación masiva desde Excel. Para dar de alta a UNA
// persona —que es lo que pasa todo el tiempo cuando entra alguien a media campaña— había que armar
// un Excel de una fila. Esto cierra ese hueco.
//
// **No crea el usuario por su cuenta**: arma la misma entrada que la importación masiva y llama a
// `approveWorkersBulk`, o sea a la función del servidor que ya estaba probada. Crear el acceso toca
// la autenticación, la lista negra de documentos y el perfil a la vez; una segunda implementación
// se habría desincronizado con la primera el día que cambie cualquiera de las tres.
// ---------------------------------------------------------------------------

export interface FormNuevoTrabajador {
  apellidos: string;
  nombres: string;
  tipoDocumento: 'DNI' | 'CE';
  documento: string;
  fechaNacimiento: string;
  telefono: string;
  correo: string;
  clave: string;
}

export function formNuevoVacio(): FormNuevoTrabajador {
  return {
    apellidos: '', nombres: '', tipoDocumento: 'DNI', documento: '',
    fechaNacimiento: '', telefono: '', correo: '', clave: generateTempPassword(),
  };
}

/**
 * Qué falta para poder crear, en el orden en que se lee el formulario. `null` = se puede.
 *
 * Devuelve el TEXTO y no un booleano: el botón apagado sin decir por qué obliga a adivinar cuál de
 * ocho campos es el que estorba. Es el mismo criterio del modal de corrección de canjes.
 */
export function loQueFaltaEnNuevoTrabajador(f: FormNuevoTrabajador): string | null {
  if (f.apellidos.trim() === '') return 'Faltan los apellidos.';
  if (f.nombres.trim() === '') return 'Faltan los nombres.';
  const doc = normalizarDocumento(f.documento);
  if (doc === '') return 'Falta el documento.';
  // Se valida sobre el NORMALIZADO —sin puntos, guiones ni ceros a la izquierda— porque es la forma
  // en que el sistema cruza documentos: aceptar acá algo que después no calza crea un trabajador
  // que existe y que ninguna planilla encuentra.
  if (!/^\d{6,12}$/.test(doc)) return 'El documento debe tener entre 6 y 12 dígitos.';
  // La clave la genera la pantalla, pero es editable: alguien puede querer dictarla por teléfono.
  if (f.clave.trim().length < 6) return 'La clave temporal necesita al menos 6 caracteres.';
  if (f.correo.trim() !== '' && !f.correo.includes('@')) return 'Ese correo no parece un correo.';
  return null;
}

/**
 * Del formulario a lo que espera el servidor.
 *
 * El documento viaja **tal como se tecleó** (solo recortado), no normalizado: el correo de acceso
 * se arma con él y la persona va a escribir su documento como lo tiene en el carné. La
 * normalización es para CRUZAR, no para guardar.
 */
export function entradaDesdeForm(f: FormNuevoTrabajador): BulkWorkerInput {
  const limpio = (v: string): string => v.trim();
  return {
    dni: limpio(f.documento),
    document_type: f.tipoDocumento,
    first_names: limpio(f.nombres),
    last_names: limpio(f.apellidos),
    phone: limpio(f.telefono),
    email: limpio(f.correo),
    birth_date: limpio(f.fechaNacimiento) === '' ? null : limpio(f.fechaNacimiento),
    // Los datos de pago NO se piden acá a propósito: viven detrás de una función auditada y no
    // tienen por qué pasar por una pantalla de alta rápida. Se completan después.
    bank: null,
    account_number: null,
    cci: null,
    temp_password: f.clave.trim(),
  };
}

/** Zona horaria de toda la lógica de fechas del proyecto. */
const TZ = 'America/Lima';

/**
 * Texto de la columna «Acceso». Nunca dice la clave —no existe en ninguna parte, la base guarda un
 * hash— sino LO ÚNICO que se puede saber y que además es lo que hace falta para actuar: si la
 * persona todavía usa la clave que le entregaron, y cuándo entró por última vez.
 */
export function textoDeAcceso(e: EstadoDeClave | undefined): { texto: string; alerta: boolean } {
  if (!e) return { texto: '—', alerta: false };
  if (e.ultimoIngreso === null) {
    return { texto: 'Nunca ha entrado · sigue con la clave que le dieron', alerta: true };
  }
  const cuando = formatInTimeZone(new Date(e.ultimoIngreso), TZ, 'dd/MM/yy HH:mm');
  if (e.sigueConClaveInicial) {
    return { texto: `Entró ${cuando}, pero NO cambió su clave`, alerta: true };
  }
  return { texto: `Entró ${cuando} · ya tiene su propia clave`, alerta: false };
}

export function ActivosTab() {
  const { roles: misRoles } = useAuth();
  /** En modo lectura no se muestran roles, estado ni el alta: el servidor los rechaza igual. */
  const editable = puedeAdministrarPadron(misRoles);

  /** `null` = el modal de alta manual está cerrado. */
  const [nuevo, setNuevo] = useState<FormNuevoTrabajador | null>(null);
  const [creando, setCreando] = useState(false);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * Permiso APARTE del rol admin (0085): solo el super admin y el jefe de operaciones ven el estado
   * de acceso de la gente y pueden cambiarle la clave. Esconder los botones es comodidad; la base
   * rechaza igual las llamadas de quien no lo tiene.
   */
  const [puedeClaves, setPuedeClaves] = useState(false);
  /** Cotizaciones Treid: solo quien administra compras ve y cambia las casillas «Comprador» y «Solicitante». */
  const [adminCompras, setAdminCompras] = useState(false);
  useEffect(() => {
    let vivo = true;
    void miAccesoCompras().then((a) => {
      if (vivo) setAdminCompras(a.adminCompras);
    });
    return () => {
      vivo = false;
    };
  }, []);
  const ROLES_DE_COMPRAS = ['comprador', 'solicitante'];
  const rolesEditables = ROLES.filter((r) => !ROLES_DE_COMPRAS.includes(r.value) || adminCompras);
  const [estados, setEstados] = useState<Map<string, EstadoDeClave>>(new Map());
  /** Clave recién puesta, en pantalla hasta que la entreguen (se copia o se manda por WhatsApp). */
  const [claveLista, setClaveLista] = useState<Credencial | null>(null);
  const [bloqueo, setBloqueo] = useState<ProfileRow | null>(null);
  const [motivo, setMotivo] = useState('');
  const [reset, setReset] = useState<ProfileRow | null>(null);
  const [nuevaClave, setNuevaClave] = useState('');
  /** Trabajador cuyo desempeño se está mirando. `null` = ninguno. */
  const [desempeno, setDesempeno] = useState<ProfileRow | null>(null);
  /** Rol por el que se filtra la lista, o `''` para no filtrar. */
  const [rolFiltro, setRolFiltro] = useState<Role | ''>('');
  /**
   * Lo que se ve con el filtro de rol puesto.
   *
   * Se calcula ANTES de las tarjetas para que el resumen, la tabla y la lista de celular cuenten lo
   * mismo: un indicador que dice 50 sobre una lista de 3 es peor que no tener indicador. La búsqueda
   * por texto la resuelve el servidor (`listWorkers`), así que este filtro es el único que vive acá.
   */
  const visibles = useMemo(
    () => (rolFiltro === '' ? rows : rows.filter((w) => (w.roles ?? []).includes(rolFiltro))),
    [rows, rolFiltro],
  );
  /**
   * Ficha que se está corrigiendo. Guarda el perfil ORIGINAL además del formulario, porque lo que
   * se manda a la base es la diferencia entre los dos: solo las columnas que de verdad cambiaron.
   */
  const [ficha, setFicha] = useState<{ perfil: ProfileRow; form: FormFichaTrabajador } | null>(null);
  const [guardandoFicha, setGuardandoFicha] = useState(false);

  /**
   * Guarda las correcciones de la ficha abierta.
   *
   * Tres cosas que no son obvias y por eso están escritas:
   *
   * - Si no cambió nada, **no escribe**. Se lo dice y cierra: un rastro de auditoría que dice «se
   *   corrigió la ficha» cuando alguien abrió y cerró la ventana es rastro falso.
   * - El aviso nombra LOS CAMPOS que cambiaron. «Guardado» a secas, en un formulario de diez
   *   casillas, no permite saber si se guardó lo que uno tocó.
   * - Después recarga, y ahí se ve el efecto del trigger `nombre_propio` de la 0103: si alguien
   *   escribió el nombre en mayúsculas, vuelve normalizado. Sin recargar, la pantalla mostraría lo
   *   tecleado y la base tendría otra cosa.
   */
  async function guardarFicha() {
    if (!ficha) return;
    const falta = loQueFaltaEnFicha(ficha.form);
    if (falta !== null) { setError(falta); return; }

    const cambios = cambiosDeFicha(ficha.perfil, ficha.form);
    const nombres = nombresDeLosCambios(cambios);
    if (nombres.length === 0) {
      setOk('No había nada que cambiar en esa ficha.');
      setFicha(null);
      return;
    }

    setGuardandoFicha(true);
    setError(null);
    try {
      await actualizarFichaTrabajador(
        ficha.perfil.id, cambios, ficha.perfil as unknown as Record<string, unknown>,
      );
      setOk(`Ficha de ${ficha.perfil.first_names} ${ficha.perfil.last_names} corregida: `
        + `${nombres.join(', ')}.`);
      setFicha(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la ficha.');
    } finally {
      setGuardandoFicha(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listWorkers(query));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * El permiso y el estado de acceso se piden UNA vez, no por fila. `estado_de_claves` devuelve la
   * tabla entera de una: son 55 filas de tres campos, y pedir 55 veces lo mismo dejaría 55 líneas
   * idénticas en la auditoría de una sola pantalla.
   *
   * Si el permiso falta no se consulta el estado: la base lo rechazaría y el error no aporta nada
   * a quien simplemente no tiene por qué verlo.
   */
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const puede = await puedeGestionarClaves();
      if (!vivo) return;
      setPuedeClaves(puede);
      if (!puede) return;
      try {
        const mapa = await estadoDeClaves();
        if (vivo) setEstados(mapa);
      } catch (e) {
        if (vivo) setError(e instanceof Error ? e.message : 'No se pudo leer el estado de acceso.');
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  async function toggleRole(worker: ProfileRow, role: string, checked: boolean) {
    const next = checked
      ? Array.from(new Set([...(worker.roles ?? []), role]))
      : (worker.roles ?? []).filter((r) => r !== role);
    setBusyId(worker.id);
    setError(null);
    try {
      await updateRoles(worker.id, next);
      setOk(`Roles actualizados para ${worker.first_names} ${worker.last_names}.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron guardar los roles.');
    } finally {
      setBusyId(null);
    }
  }

  async function changeStatus(worker: ProfileRow, status: WorkerStatus) {
    if (status === 'bloqueado') {
      setMotivo('');
      setBloqueo(worker);
      return;
    }
    setBusyId(worker.id);
    setError(null);
    try {
      await setStatus(worker.id, status);
      setOk(`Estado de ${worker.first_names} ${worker.last_names}: ${STATUS_LABEL[status]}.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el estado.');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmBloqueo() {
    if (!bloqueo || motivo.trim().length === 0) return;
    setBusyId(bloqueo.id);
    setError(null);
    try {
      await setStatus(bloqueo.id, 'bloqueado', motivo.trim());
      setOk(`${bloqueo.first_names} ${bloqueo.last_names} quedó bloqueado.`);
      setBloqueo(null);
      setMotivo('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo bloquear.');
    } finally {
      setBusyId(null);
    }
  }

  /** Vuelve a leer el estado de acceso tras cambiar una clave, para que la columna no mienta. */
  async function recargarEstados(): Promise<void> {
    if (!puedeClaves) return;
    try {
      setEstados(await estadoDeClaves());
    } catch {
      // La clave YA se cambió; no poder refrescar la columna no invalida la acción.
    }
  }

  /**
   * Un clic: genera una clave, la aplica y abre WhatsApp con el mensaje listo.
   *
   * NO se puede reenviar «la clave que tenía»: la base guarda un hash, así que no existe. Se manda
   * una nueva, que es lo que el usuario aceptó al pedirlo. Al entrar con ella el sistema la obliga
   * a crear la suya — eso lo marca el servidor, no esta pantalla.
   *
   * ⚠️ La pestaña de WhatsApp se abre **antes** de la llamada, todavía dentro del clic. Abrirla
   * después de un `await` la convierte en una ventana emergente sin gesto del usuario detrás, y el
   * navegador la bloquea: el jefe vería «clave cambiada» y ningún WhatsApp, con la clave nueva ya
   * aplicada y sin forma de hacérsela llegar.
   */
  async function enviarClavePorWhatsapp(w: ProfileRow) {
    const estado = estados.get(w.id);
    if (estado && !estado.sigueConClaveInicial) {
      const seguir = window.confirm(
        `${w.first_names} ${w.last_names} ya creó su propia clave. `
        + 'Enviarle una nueva por WhatsApp REEMPLAZA la suya y tendrá que volver a crearla. '
        + '¿Continuar?',
      );
      if (!seguir) return;
    }
    const clave = generateTempPassword();
    const ventana = window.open('', '_blank');
    setBusyId(w.id);
    setError(null);
    try {
      await resetPassword(w.dni, clave);
      const datos: Credencial = {
        nombre: `${w.first_names} ${w.last_names}`,
        documento: w.dni,
        telefono: w.phone,
        clave,
        campania: estado?.campania ?? null,
        tienda: estado?.tienda ?? null,
        horario: estado?.horario ?? null,
      };
      const enlace = enlaceDeAvisoDeAcceso({
        nombre: datos.nombre,
        documento: datos.documento,
        clave: datos.clave,
        url: window.location.origin,
        telefono: datos.telefono,
        campania: datos.campania,
        tienda: datos.tienda,
        horario: datos.horario,
      });
      if (ventana && enlace) ventana.location.href = enlace;
      else ventana?.close();
      // La ventana queda igual: la clave tiene que poder copiarse aunque WhatsApp no abra.
      setClaveLista(datos);
      await recargarEstados();
    } catch (e) {
      ventana?.close();
      setError(e instanceof Error ? e.message : 'No se pudo enviar la clave.');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmReset() {
    if (!reset) return;
    setBusyId(reset.id);
    setError(null);
    try {
      await resetPassword(reset.dni, nuevaClave);
      // La clave se queda en pantalla hasta que se cierre a mano: es el ÚNICO momento en que
      // existe en algún lugar legible. Si se va con el banner y nadie la anotó, se perdió y hay
      // que volver a cambiarla.
      setClaveLista({
        nombre: `${reset.first_names} ${reset.last_names}`,
        documento: reset.dni,
        telefono: reset.phone,
        clave: nuevaClave,
        campania: estados.get(reset.id)?.campania ?? null,
        tienda: estados.get(reset.id)?.tienda ?? null,
        horario: estados.get(reset.id)?.horario ?? null,
      });
      setReset(null);
      await recargarEstados();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo resetear la clave.');
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Crea UN trabajador por la misma vía que la importación masiva.
   *
   * El resultado se lee del arreglo y no del `throw`: la función del servidor responde 200 con
   * `{ok:false, error:'dni_duplicado'}` para los rechazos de negocio, así que confiar solo en la
   * excepción daría por creado a alguien que no se creó — que es el peor error posible acá, porque
   * la clave temporal se dicta y recién falla cuando la persona intenta entrar.
   */
  async function crearNuevo() {
    if (!nuevo) return;
    if (loQueFaltaEnNuevoTrabajador(nuevo) !== null) return;
    setCreando(true);
    setError(null);
    setOk(null);
    try {
      const [r] = await approveWorkersBulk([entradaDesdeForm(nuevo)]);
      if (!r || !r.ok) {
        setError(r ? translateApiError(r.error ?? 'desconocido') : 'Sin respuesta del servidor.');
        return;
      }
      // La clave va en el mensaje de éxito porque es el único momento en que se puede ver: se
      // guarda cifrada y después ya no hay forma de recuperarla, solo de reemplazarla.
      setOk(
        `Trabajador creado: ${nuevo.nombres.trim()} ${nuevo.apellidos.trim()} · `
        + `entra con ${nuevo.documento.trim()} y la clave ${nuevo.clave.trim()}. `
        + 'El sistema le va a pedir cambiarla al primer ingreso.',
      );
      setNuevo(null);
      setQuery(nuevo.documento.trim());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear el trabajador.');
    } finally {
      setCreando(false);
    }
  }

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por documento, nombre o correo"
          className="w-full min-w-0 rounded border border-gray-300 px-3 py-2 text-sm sm:w-80"
        />
        <button
          type="button"
          onClick={() => void load()}
          className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700"
        >
          Buscar
        </button>
        {/* El filtro de rol vive en el cliente y la búsqueda por texto en el servidor: por eso este
            no necesita apretar «Buscar». Es lo que el usuario pidió el 11-set para poder aislar a
            los supervisores del resto del padrón. */}
        <select
          aria-label="Filtrar por rol"
          value={rolFiltro}
          onChange={(e) => setRolFiltro(e.target.value as Role | '')}
          className="rounded border border-gray-300 px-2 py-2 text-sm"
        >
          <option value="">Todos los roles</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        {rolFiltro !== '' && (
          <span className="self-center text-sm text-gray-600">
            Mostrando {visibles.length} de {rows.length}
          </span>
        )}
        {/* El alta manual vive acá y no en su propia solapa a propósito: se usa cuando ya se buscó
            a la persona y no apareció, que es exactamente el momento en que uno descubre que hay
            que darla de alta. */}
        {editable && (
          <button
            type="button"
            onClick={() => { setOk(null); setError(null); setNuevo(formNuevoVacio()); }}
            className="whitespace-nowrap rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white"
          >
            + Agregar trabajador
          </button>
        )}
      </div>

      {/* Mismos huecos que en las solicitudes, pero acá ya están adentro del sistema: son las
          personas a las que se les puede asignar un turno hoy. El bloqueado se cuenta aparte porque
          sigue en la lista y no se le puede asignar nada. */}
      {!loading && visibles.length > 0 && (
        <TarjetasResumen indicadores={[
          { titulo: 'Trabajadores', valor: visibles.length,
            detalle: query.trim() === '' ? undefined : 'con la búsqueda puesta' },
          { titulo: 'Bloqueados', valor: visibles.filter((w) => w.status === 'bloqueado').length,
            tono: 'malo', detalle: 'no se les puede asignar' },
          { titulo: 'Sin selfie', valor: visibles.filter((w) => !w.photo_path).length,
            tono: 'atencion' },
          { titulo: 'Sin documento',
            valor: visibles.filter((w) => !w.doc_front_path || !w.doc_back_path).length,
            tono: 'atencion', detalle: 'falta una cara o las dos' },
          { titulo: 'Impulsadoras',
            valor: visibles.filter((w) => (w.roles ?? []).includes('recurso')).length,
            detalle: 'con rol de campo' },
        ]} />
      )}

      {ok && <p className="mb-3 rounded bg-green-50 p-2 text-sm text-green-800">{ok}</p>}
      {error && <p className="mb-3 rounded bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {loading && <p className="text-sm text-gray-500">Cargando trabajadores…</p>}
      {!loading && visibles.length === 0 && (
        <p className="text-sm text-gray-500">No hay trabajadores que coincidan con la búsqueda.</p>
      )}

      {/* En celular: una tarjeta por trabajador (sin desplazamiento horizontal). */}
      {!loading && visibles.length > 0 && (
        <div className="space-y-2 lg:hidden">
          {visibles.map((w) => (
            <Fragment key={w.id}>
            <FilaTarjeta
              titulo={`${w.first_names} ${w.last_names}`}
              campos={[
                { etiqueta: 'Documento', valor: `${w.document_type} ${w.dni}` },
                { etiqueta: 'Correo', valor: w.email },
                ...(puedeClaves
                  ? [{ etiqueta: 'Acceso', valor: textoDeAcceso(estados.get(w.id)).texto }]
                  : []),
                w.status === 'bloqueado' && w.block_reason
                  ? {
                      etiqueta: 'Motivo',
                      valor: <span className="text-red-600">{w.block_reason}</span>,
                    }
                  : null,
                {
                  etiqueta: 'Roles',
                  valor: !editable ? (
                    <span className="text-xs">
                      {ROLES.filter((r) => (w.roles ?? []).includes(r.value))
                        .map((r) => r.label)
                        .join(', ') || '—'}
                    </span>
                  ) : (
                    <>
                      <span className="flex flex-wrap gap-x-3">
                        {rolesEditables.map((r) => (
                          <label
                            key={r.value}
                            className="inline-flex min-h-[44px] items-center gap-1 text-xs"
                          >
                            <input
                              type="checkbox"
                              checked={(w.roles ?? []).includes(r.value)}
                              disabled={busyId === w.id}
                              onChange={(e) => void toggleRole(w, r.value, e.target.checked)}
                            />
                            {r.label}
                          </label>
                        ))}
                      </span>
                      {compradorConRecurso(w.roles ?? []) && (
                        <span className="block text-xs text-amber-700">
                          Comprador con «Recurso»: quítale Recurso para que ningún coordinador pueda cambiarle la clave.
                        </span>
                      )}
                    </>
                  ),
                },
                {
                  etiqueta: 'Estado',
                  valor: !editable ? (
                    <span className="text-xs">{w.status}</span>
                  ) : (
                    <select
                      value={w.status}
                      disabled={busyId === w.id}
                      onChange={(e) => void changeStatus(w, e.target.value as WorkerStatus)}
                      className="min-h-[44px] rounded border border-gray-300 px-2 py-1 text-xs"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                      <option value="bloqueado">Bloqueado</option>
                    </select>
                  ),
                },
              ]}
              acciones={
                editable || (puedeClaves && estados.has(w.id)) ? (
                  <div className="flex flex-wrap gap-2">
                    {editable && (
                      <button
                        type="button"
                        onClick={() => {
                          setOk(null); setError(null);
                          setFicha({ perfil: w, form: fichaDesdePerfil(w) });
                        }}
                        className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700"
                      >
                        Editar ficha
                      </button>
                    )}
                    {puedeClaves && estados.has(w.id) && (
                    <button
                      type="button"
                      disabled={busyId === w.id}
                      onClick={() => {
                        setNuevaClave(generateTempPassword());
                        setReset(w);
                      }}
                      className="rounded bg-blue-600 px-3 py-1 text-xs text-white disabled:opacity-50"
                    >
                      Cambiar clave
                    </button>
                    )}
                    {puedeClaves && estados.has(w.id) && telefonoParaWhatsapp(w.phone) && (
                      <button
                        type="button"
                        disabled={busyId === w.id}
                        onClick={() => void enviarClavePorWhatsapp(w)}
                        className="rounded bg-green-600 px-3 py-1 text-xs text-white disabled:opacity-50"
                      >
                        Enviar por WhatsApp
                      </button>
                    )}
                  </div>
                ) : null
              }
            />
            {desempeno?.id === w.id && (
              <DesempenoTrabajador
                workerId={w.id}
                nombre={`${w.first_names} ${w.last_names}`}
              />
            )}
            </Fragment>
          ))}
        </div>
      )}

      {!loading && visibles.length > 0 && (
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="py-2">Documento</th>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Roles</th>
                <th>Estado</th>
                {puedeClaves && <th>Acceso</th>}
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((w) => (
                <Fragment key={w.id}>
                <tr className="border-b border-gray-100 align-top">
                  <td className="py-2">
                    {w.document_type} {w.dni}
                  </td>
                  <td>
                    {w.first_names} {w.last_names}
                    {w.status === 'bloqueado' && w.block_reason && (
                      <p className="text-xs text-red-600">Motivo: {w.block_reason}</p>
                    )}
                    <button
                      type="button"
                      onClick={() => setDesempeno(desempeno?.id === w.id ? null : w)}
                      className="block text-xs text-blue-700 underline"
                    >
                      {desempeno?.id === w.id ? 'ocultar desempeño' : 'ver desempeño'}
                    </button>
                  </td>
                  <td>{w.email}</td>
                  <td>
                    {editable ? (
                      <>
                        {rolesEditables.map((r) => (
                          <label key={r.value} className="mr-3 inline-flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={(w.roles ?? []).includes(r.value)}
                              disabled={busyId === w.id}
                              onChange={(e) => void toggleRole(w, r.value, e.target.checked)}
                            />
                            {r.label}
                          </label>
                        ))}
                        {compradorConRecurso(w.roles ?? []) && (
                          <span className="block text-xs text-amber-700">
                            Comprador con «Recurso»: quítale Recurso para que ningún coordinador pueda cambiarle la clave.
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-600">
                        {ROLES.filter((r) => (w.roles ?? []).includes(r.value))
                          .map((r) => r.label)
                          .join(', ') || '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editable ? (
                      <select
                        value={w.status}
                        disabled={busyId === w.id}
                        onChange={(e) => void changeStatus(w, e.target.value as WorkerStatus)}
                        className="rounded border border-gray-300 px-2 py-1 text-xs"
                      >
                        <option value="activo">Activo</option>
                        <option value="inactivo">Inactivo</option>
                        <option value="bloqueado">Bloqueado</option>
                      </select>
                    ) : (
                      <span className="text-xs text-gray-600">{w.status}</span>
                    )}
                  </td>
                  {puedeClaves && (
                    <td className="max-w-[15rem] text-xs">
                      {(() => {
                        const acc = textoDeAcceso(estados.get(w.id));
                        return (
                          <span className={acc.alerta ? 'text-amber-700' : 'text-gray-600'}>
                            {acc.texto}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className="whitespace-nowrap">
                    {/*
                      Los botones de clave no dependen solo de tener permiso, sino de tenerlo SOBRE
                      ESTA PERSONA: `estado_de_claves` devuelve únicamente las filas sobre las que
                      quien mira puede actuar (0086), así que un coordinador no ve el botón en la
                      fila de un administrador. La base lo rechaza igual; esto evita ofrecer algo
                      que va a fallar.

                      «Editar» es del jefe de operaciones (`editable`) y va aparte: corregir un
                      distrito mal escrito no tiene nada que ver con poder cambiarle la clave.
                    */}
                    {editable && (
                      <button
                        type="button"
                        onClick={() => {
                          setOk(null); setError(null);
                          setFicha({ perfil: w, form: fichaDesdePerfil(w) });
                        }}
                        className="mb-1 mr-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                      >
                        Editar
                      </button>
                    )}
                    {puedeClaves && estados.has(w.id) ? (
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          disabled={busyId === w.id}
                          onClick={() => {
                            setNuevaClave(generateTempPassword());
                            setReset(w);
                          }}
                          className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                        >
                          Cambiar clave
                        </button>
                        {telefonoParaWhatsapp(w.phone) ? (
                          <button
                            type="button"
                            disabled={busyId === w.id}
                            onClick={() => void enviarClavePorWhatsapp(w)}
                            className="rounded bg-green-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                            title="Genera una clave nueva, la aplica y abre WhatsApp con el mensaje"
                          >
                            Enviar por WhatsApp
                          </button>
                        ) : (
                          <span className="self-center text-xs text-gray-400" title="Sin celular válido en su ficha">
                            sin celular
                          </span>
                        )}
                      </div>
                    ) : (
                      !editable && <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
                {/*
                  🔴 El panel va ACÁ, pegado a su fila, y no al final de la pestaña.

                  Hasta el 11-set se dibujaba después de la tabla entera y de todos los modales: con
                  cincuenta trabajadores, tocar «ver desempeño» en la fila 3 lo abría unos tres mil
                  píxeles más abajo. El usuario lo reportó como «está fallando el link» — y el enlace
                  funcionaba: el panel se abría donde no se podía ver. Ese mismo día el mismo modo de
                  falla apareció otras dos veces. **Una respuesta fuera de la vista es, para quien la
                  usa, ninguna respuesta.**
                */}
                {desempeno?.id === w.id && (
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <td colSpan={puedeClaves ? 7 : 6} className="p-2">
                      <DesempenoTrabajador
                        workerId={w.id}
                        nombre={`${w.first_names} ${w.last_names}`}
                      />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bloqueo && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900">Bloquear trabajador</h2>
            <p className="mb-3 text-sm text-gray-500">
              {bloqueo.first_names} {bloqueo.last_names} no podrá iniciar sesión hasta que lo
              reactives.
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="motivo-bloqueo">
              Motivo (obligatorio)
            </label>
            <textarea
              id="motivo-bloqueo"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              className="mb-4 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setBloqueo(null)}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={motivo.trim().length === 0 || busyId === bloqueo.id}
                onClick={() => void confirmBloqueo()}
                className="w-2/3 rounded bg-red-600 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Bloquear
              </button>
            </div>
          </div>
        </div>
      )}

      {claveLista && (
        <PanelCredencial datos={claveLista} onClose={() => setClaveLista(null)} />
      )}

      {reset && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900">Cambiar clave</h2>
            <p className="mb-3 text-sm text-gray-500">
              {reset.first_names} {reset.last_names} · {reset.document_type} {reset.dni}
            </p>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="clave-nueva">
              Clave nueva
              <span className="ml-1 font-normal text-gray-400">
                (escríbela tú, o genera una)
              </span>
            </label>
            <div className="mb-4 flex gap-2">
              <input
                id="clave-nueva"
                type="text"
                value={nuevaClave}
                onChange={(e) => setNuevaClave(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => setNuevaClave(generateTempPassword())}
                className="whitespace-nowrap rounded border border-gray-300 px-2 text-sm text-gray-700"
              >
                Generar
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setReset(null)}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={nuevaClave.length < 6 || busyId === reset.id}
                onClick={() => void confirmReset()}
                className="w-2/3 rounded bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                Aplicar clave nueva
              </button>
            </div>
          </div>
        </div>
      )}

      {ficha && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900">Corregir la ficha</h2>
            <p className="mb-3 text-sm text-gray-500">
              Para arreglar lo que se llenó mal. Se guarda solo lo que cambies, y queda constancia
              interna de qué campo se corrigió.
            </p>

            {/* El documento se muestra y NO se edita, y se dice por qué: la persona entra al
                sistema con su documento, así que cambiarlo acá la dejaría sin poder entrar con una
                ficha que se ve perfecta. La base lo prohíbe igual por trigger. */}
            <p className="mb-4 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
              <strong>{ficha.perfil.document_type} {ficha.perfil.dni}</strong> — el documento no se
              corrige desde acá: es el usuario con el que entra al sistema. Si está mal, hay que
              cambiar también su cuenta de acceso.
            </p>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2 flex flex-col">
                Apellidos
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.apellidos} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, apellidos: e.target.value },
                  })} />
              </label>
              <label className="col-span-2 flex flex-col">
                Nombres
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.nombres} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, nombres: e.target.value },
                  })} />
              </label>
              <label className="flex flex-col">
                Fecha de nacimiento
                <input type="date" className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.fechaNacimiento} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, fechaNacimiento: e.target.value },
                  })} />
              </label>
              <label className="flex flex-col">
                Teléfono
                <input type="tel" className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.telefono} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, telefono: e.target.value },
                  })} />
              </label>
              <label className="col-span-2 flex flex-col">
                Correo de contacto
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.correo} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, correo: e.target.value },
                  })} />
              </label>
              <label className="flex flex-col">
                Departamento
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.departamento} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, departamento: e.target.value },
                  })} />
              </label>
              <label className="flex flex-col">
                Provincia
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.provincia} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, provincia: e.target.value },
                  })} />
              </label>
              <label className="flex flex-col">
                Distrito
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.distrito} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, distrito: e.target.value },
                  })} />
              </label>
              <label className="flex flex-col">
                Zona
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.zona} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, zona: e.target.value },
                  })} />
              </label>
              <label className="col-span-2 flex flex-col">
                Dirección
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={ficha.form.direccion} autoComplete="off"
                  onChange={(e) => setFicha({
                    ...ficha, form: { ...ficha.form, direccion: e.target.value },
                  })} />
              </label>
            </div>

            {/* El correo de la ficha es de CONTACTO y no el usuario con el que entra: de 72 fichas,
                31 tienen uno distinto del de su cuenta. Quien lo edita asume lo contrario. */}
            <p className="mt-3 text-xs text-gray-500">
              El correo es de contacto: cambiarlo no cambia con qué entra al sistema.
            </p>

            {/* El aviso de lo que falta se muestra ANTES de apretar, no después de un viaje al
                servidor: el botón apagado sin decir cuál de diez casillas estorba obliga a
                adivinar. */}
            {loQueFaltaEnFicha(ficha.form) !== null && (
              <p role="alert" className="mt-3 text-sm text-amber-800">
                {loQueFaltaEnFicha(ficha.form)}
              </p>
            )}

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => { setFicha(null); setError(null); }}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={guardandoFicha || loQueFaltaEnFicha(ficha.form) !== null}
                onClick={() => void guardarFicha()}
                className="w-2/3 rounded bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {guardandoFicha ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {nuevo && (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-bold text-gray-900">Agregar trabajador</h2>
            <p className="mb-4 text-sm text-gray-500">
              Para dar de alta a alguien que no se registró por su cuenta. Entra con su documento y
              el sistema le pide cambiar la clave la primera vez.
            </p>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2 flex flex-col">
                Apellidos
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.apellidos} autoComplete="off"
                  onChange={(e) => setNuevo({ ...nuevo, apellidos: e.target.value })} />
              </label>
              <label className="col-span-2 flex flex-col">
                Nombres
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.nombres} autoComplete="off"
                  onChange={(e) => setNuevo({ ...nuevo, nombres: e.target.value })} />
              </label>

              <label className="flex flex-col">
                Tipo
                <select className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.tipoDocumento}
                  onChange={(e) => setNuevo({
                    ...nuevo, tipoDocumento: e.target.value as 'DNI' | 'CE',
                  })}>
                  <option value="DNI">DNI</option>
                  <option value="CE">Carné de extranjería</option>
                </select>
              </label>
              <label className="flex flex-col">
                Documento
                <input className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.documento} inputMode="numeric" autoComplete="off"
                  onChange={(e) => setNuevo({ ...nuevo, documento: e.target.value })} />
              </label>

              <label className="flex flex-col">
                Fecha de nacimiento <span className="text-xs text-gray-500">(opcional)</span>
                <input type="date" className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.fechaNacimiento}
                  onChange={(e) => setNuevo({ ...nuevo, fechaNacimiento: e.target.value })} />
              </label>
              <label className="flex flex-col">
                Teléfono <span className="text-xs text-gray-500">(opcional)</span>
                <input type="tel" inputMode="tel"
                  className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.telefono}
                  onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })} />
              </label>

              <label className="col-span-2 flex flex-col">
                Correo de contacto <span className="text-xs text-gray-500">(opcional)</span>
                <input type="email" className="mt-1 rounded border border-gray-300 px-3 py-2"
                  value={nuevo.correo} autoComplete="off"
                  onChange={(e) => setNuevo({ ...nuevo, correo: e.target.value })} />
                {/* Se aclara porque es la confusión más natural: acá TODOS entran con documento,
                    también los de oficina. Este correo es solo para ubicar a la persona. */}
                <span className="mt-1 text-xs text-gray-500">
                  No es el usuario para entrar: eso siempre es el documento.
                </span>
              </label>

              <label className="col-span-2 flex flex-col">
                Clave temporal
                <span className="mt-1 flex gap-2">
                  <input className="w-full rounded border border-gray-300 px-3 py-2 font-mono"
                    value={nuevo.clave} autoComplete="off"
                    onChange={(e) => setNuevo({ ...nuevo, clave: e.target.value })} />
                  <button type="button"
                    onClick={() => setNuevo({ ...nuevo, clave: generateTempPassword() })}
                    className="whitespace-nowrap rounded border border-gray-300 px-2 text-sm text-gray-700">
                    Generar
                  </button>
                </span>
              </label>
            </div>

            {/* El porqué a la vista y no un botón apagado sin explicación: con ocho campos, adivinar
                cuál estorba es el camino corto a que alguien cierre el modal y desista. */}
            {loQueFaltaEnNuevoTrabajador(nuevo) !== null && (
              <p className="mt-3 text-xs text-amber-800">{loQueFaltaEnNuevoTrabajador(nuevo)}</p>
            )}
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-4 flex gap-2">
              <button type="button" disabled={creando} onClick={() => setNuevo(null)}
                className="w-1/3 rounded border border-gray-300 py-2 text-sm text-gray-700">
                Cancelar
              </button>
              <button type="button"
                disabled={creando || loQueFaltaEnNuevoTrabajador(nuevo) !== null}
                onClick={() => void crearNuevo()}
                className="w-2/3 rounded bg-blue-600 py-2 text-sm font-medium text-white disabled:opacity-50">
                {creando ? 'Creando usuario…' : 'Crear trabajador'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default function Trabajadores() {
  const { roles } = useAuth();
  /**
   * El coordinador entra en modo lectura (0086 + matriz de permisos): ve el padrón y puede mandar
   * claves, pero no da de alta, no cambia roles ni estados, y no aprueba solicitudes.
   *
   * Por eso ve UNA sola solapa. Mostrarle «Solicitudes» e «Importar» sería ofrecerle dos pantallas
   * que el servidor le va a rechazar entera.
   */
  const apruebaSolicitudes = puedeAprobarSolicitudes(roles);
  const administraPadron = puedeAdministrarPadron(roles);
  const [tab, setTab] = useState<'solicitudes' | 'activos' | 'importar'>(
    apruebaSolicitudes ? 'solicitudes' : 'activos',
  );
  const tabClass = (value: typeof tab) =>
    `px-3 py-2 text-sm ${
      tab === value ? 'border-b-2 border-blue-600 font-semibold text-blue-600' : 'text-gray-600'
    }`;
  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900">Trabajadores</h1>
      {/* Cada solapa se muestra solo si su trabajo le corresponde. El coordinador ve dos:
          la cola de postulantes y el padrón. «Importar» es alta masiva, del jefe. */}
      <div className="mb-4 flex gap-2 border-b border-gray-200">
        {apruebaSolicitudes && (
          <button type="button" onClick={() => setTab('solicitudes')} className={tabClass('solicitudes')}>
            Solicitudes
          </button>
        )}
        <button type="button" onClick={() => setTab('activos')} className={tabClass('activos')}>
          Activos
        </button>
        {administraPadron && (
          <button type="button" onClick={() => setTab('importar')} className={tabClass('importar')}>
            Importar
          </button>
        )}
      </div>
      {tab === 'solicitudes' && apruebaSolicitudes ? (
        <SolicitudesTab />
      ) : tab === 'importar' && administraPadron ? (
        <TrabajadoresImportar />
      ) : (
        <ActivosTab />
      )}
    </div>
  );
}
